import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

export const DEFAULT_SORT_BY_PARAM = 'sortBy';

/**
 * URL-backed page, sort, and search state for {@link AppTableV2} tables.
 * Page and sort are read from the query string (history-navigable); search is
 * driven by {@link AppSearchField} writing the same keys via
 * `setSearchAndResetPage` (or an equivalent atomic URL writer).
 */
export const useTablePaginationState = ({
  defaultSortBy,
  paginationKey,
  searchKey,
  sortByKey = DEFAULT_SORT_BY_PARAM,
}: {
  /** Fallback when the sort param is absent from the URL. */
  defaultSortBy: string;
  /** URL query param this table reads/writes its page number from. */
  paginationKey: string;
  /** URL query param {@link AppSearchField} reads/writes for this table. */
  searchKey: string;
  /**
   * URL query param for this table's sort. Defaults to the shared `sortBy`
   * key; namespaced tables (Royalties ANZ) pass a dedicated key so other
   * AppTableV2 pages cannot poison the first fetch.
   */
  sortByKey?: string;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Math.max(1, Number(searchParams.get(paginationKey)) || 1);
  const sortBy = searchParams.get(sortByKey) || defaultSortBy;
  const search = searchParams.get(searchKey) ?? '';

  const setPageAndUrl = useCallback(
    (nextPage: number) => {
      setSearchParams(
        (prev) => {
          const updated = new URLSearchParams(prev);

          if (nextPage <= 1) updated.delete(paginationKey);
          else updated.set(paginationKey, String(nextPage));

          return updated;
        },
        { replace: true },
      );
    },
    [paginationKey, setSearchParams],
  );

  // AppTableV2 notifies here when URL page/sort change (incl. sort headers).
  // Skip setSearchParams entirely when the URL already matches — returning
  // `prev` from the updater still navigates in react-router.
  const handlePageRequest = useCallback(
    (nextPage: number, nextSortBy?: string) => {
      const currentPage = Math.max(
        1,
        Number(searchParams.get(paginationKey)) || 1,
      );
      const targetPage = Math.max(1, nextPage || 1);
      const sortUnchanged =
        nextSortBy === undefined ||
        (searchParams.get(sortByKey) || defaultSortBy) === nextSortBy;

      if (currentPage === targetPage && sortUnchanged) return;

      setSearchParams(
        (prev) => {
          const updated = new URLSearchParams(prev);

          if (targetPage <= 1) updated.delete(paginationKey);
          else updated.set(paginationKey, String(targetPage));

          if (nextSortBy !== undefined) {
            updated.set(sortByKey, nextSortBy);
          }

          return updated;
        },
        { replace: true },
      );
    },
    [defaultSortBy, paginationKey, searchParams, setSearchParams, sortByKey],
  );

  const setSearchAndResetPage = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          const updated = new URLSearchParams(prev);

          if (value) updated.set(searchKey, value);
          else updated.delete(searchKey);

          // Drop page in the same navigation so the list query does not fire
          // once for the old page and again for page 1 (Titles `setFilterParam`).
          updated.delete(paginationKey);

          return updated;
        },
        { replace: true },
      );
    },
    [paginationKey, searchKey, setSearchParams],
  );

  return {
    handlePageRequest,
    page,
    search,
    setPageAndUrl,
    setSearchAndResetPage,
    sortBy,
  };
};
